import type { ReactNode } from "react";
import { Etapas, Selo, type Tom } from "./Basicos";
import { CopiarValor, Fatos, type Fato } from "./Pagina";

export type PassoDoPlano = { titulo: string; texto: ReactNode; tom?: "info" | "sucesso" | "alerta" | "erro" };

/** Um dado da conta na plataforma que a pessoa digita em outro lugar (subconta, mesa): grande, em mono, com copiar. */
export type DadoDaConta = { rotulo: string; valor: string; detalhe?: ReactNode; copiar?: boolean };

/**
 * O proximo passo de um plano: rotulo, titulo, o texto e as acoes, pintado pelo tom. Serve o cartao do plano e o
 * destaque do Inicio (spec 038, secao 3), com o mesmo texto nos dois lugares.
 */
export function ProximoPasso({ passo, acoes, rotulo = "Próximo passo", destaque }: { passo: PassoDoPlano; acoes?: ReactNode; rotulo?: string; /** Maior, pro topo do Inicio. */ destaque?: boolean }) {
  return (
    <div className={`mtc-plano__passo mtc-plano__passo--${passo.tom ?? "info"}${destaque ? " mtc-plano__passo--destaque" : ""}`}>
      <span className="mtc-rotulo">{rotulo}</span>
      <strong>{passo.titulo}</strong>
      <p>{passo.texto}</p>
      {acoes && <div className="mtc-plano__acoes mtc-plano__passo-acoes">{acoes}</div>}
    </div>
  );
}

/**
 * Um plano (matricula) numa tela so: nome, status, progresso das etapas, a conta na plataforma em destaque (subconta
 * e mesa com copiar; na Global, a conta na corretora com o saldo logo abaixo), os fatos (prazo, inicio, mensalidade) e
 * o proximo passo com a acao. Nao sabe marca nem regra.
 */
export function CartaoDePlano({ id, titulo, subtitulo, status, etiquetas, etapas, conta, saldo, fatos, passo, acoes, children, destaque }: { id?: string; titulo: string; subtitulo?: ReactNode; status: { rotulo: string; tom: Tom }; etiquetas?: ReactNode; etapas?: { lista: string[]; atual: number }; conta?: DadoDaConta[]; /** Logo abaixo da conta: o saldo na corretora (`SaldoNaCorretora`, spec 043). */ saldo?: ReactNode; fatos: Fato[]; passo?: PassoDoPlano; acoes?: ReactNode; children?: ReactNode; destaque?: boolean }) {
  return (
    <article id={id} className={`mtc-cartao mtc-plano${destaque ? " mtc-plano--destaque" : ""}`} aria-label={titulo}>
      <header className="mtc-plano__cabeca">
        <div className="mtc-plano__nome">
          <h2 className="mtc-plano__titulo">{titulo}</h2>
          {subtitulo && <span className="mtc-plano__subtitulo">{subtitulo}</span>}
        </div>
        <div className="mtc-plano__selos"><Selo tom={status.tom}>{status.rotulo}</Selo>{etiquetas}</div>
      </header>
      {etapas && etapas.lista.length > 0 && <Etapas etapas={etapas.lista} atual={etapas.atual} />}
      {conta && conta.length > 0 && (
        <dl className="mtc-plano__conta">
          {conta.map((c) => (
            <div key={c.rotulo}>
              <dt>{c.rotulo}</dt>
              <dd>
                <span className="mtc-plano__conta-linha">
                  <span className={c.copiar ? "mtc-mono mtc-plano__conta-valor" : "mtc-plano__conta-valor mtc-plano__conta-valor--texto"}>{c.valor}</span>
                  {c.copiar && <CopiarValor valor={c.valor} rotulo={`Copiar ${c.rotulo.toLowerCase()}`} classe="mtc-plano__copiar" />}
                </span>
                {c.detalhe && <small>{c.detalhe}</small>}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {saldo}
      {fatos.length > 0 && <Fatos itens={fatos} />}
      {passo && <ProximoPasso passo={passo} />}
      {children}
      {acoes && <div className="mtc-plano__acoes">{acoes}</div>}
    </article>
  );
}
